import { useCallback, useMemo, useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery, keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { CalendarClient, type CalendarSearch } from "@/features/calendar/CalendarClient";
import {
  getCalendarAccountStatus,
  listCalendar,
  listCalendarFrom,
  listCalendarSources,
  type CalendarViewResult,
  disconnectCalendarAccount,
  calendarKeys,
} from "@/features/calendar/calendar.api";
import { listTasksDueBetween } from "@/features/tasks/tasks.api";
import { applyParamUpdates } from "@/lib/searchParams";
import {
  parseLocal,
  toLocalIso,
  startOfWeekMonday,
  endOfWeekMonday,
  startOfMonth,
  endOfMonth,
  addDays,
  setTime,
} from "@/lib/dates";
import { hasModule, firstAllowedRoute } from "@/lib/auth";

// View preference, not authorisation: the server decides what may be read.
const CALENDAR_SOURCES_KEY = "wlx:calendar:sources";
import type { CalendarTaskChip } from "@/features/calendar/CalendarGrid";

export const Route = createFileRoute("/_shell/calendar/")({
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "calendar")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  validateSearch: (search: Record<string, unknown>): CalendarSearch => {
    const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
    return {
      view: str(search.view),
      date: str(search.date),
      connect: str(search.connect),
      eventId: str(search.eventId),
    };
  },
  component: CalendarScreen,
});

function computeRange(view: string, anchor: Date): { from: string; to: string } {
  if (view === "month") {
    return {
      from: toLocalIso(startOfWeekMonday(startOfMonth(anchor))),
      to: toLocalIso(endOfWeekMonday(endOfMonth(anchor))),
    };
  }
  if (view === "day") {
    return { from: toLocalIso(setTime(anchor, 0, 0)), to: toLocalIso(setTime(anchor, 23, 59)) };
  }
  if (view === "agenda") {
    return { from: toLocalIso(setTime(anchor, 0, 0)), to: toLocalIso(setTime(addDays(anchor, 30), 23, 59)) };
  }
  return { from: toLocalIso(startOfWeekMonday(anchor)), to: toLocalIso(endOfWeekMonday(anchor)) };
}

function CalendarScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const { session } = Route.useRouteContext();
  const canTasks = hasModule(session, "tasks");

  const view = search.view || "week";
  const anchor = search.date ? parseLocal(`${search.date}T00:00:00`) : new Date();
  const { from, to } = computeRange(view, anchor);

  const statusQuery = useQuery({
    queryKey: calendarKeys.account,
    queryFn: getCalendarAccountStatus,
  });
  const ready = Boolean(statusQuery.data?.connected && statusQuery.data.canCalendar && !statusQuery.data.needsReconnect);

  // Calendars in this mailbox: your own plus any a colleague shared with you.
  const sourcesQuery = useQuery({
    queryKey: calendarKeys.sources,
    queryFn: listCalendarSources,
    enabled: ready,
    staleTime: 5 * 60_000,
  });
  const sources = sourcesQuery.data ?? [];

  // Which extra calendars are switched on. Kept in the browser: it is a view
  // preference, and reading it can never widen what the server returns.
  const [extraIds, setExtraIds] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(CALENDAR_SOURCES_KEY);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  });
  // Drop ids that no longer exist (a share was revoked) once the list is known.
  const activeExtraIds = useMemo(
    () => extraIds.filter((id) => sources.some((s) => s.id === id && !s.isDefault)),
    [extraIds, sources],
  );
  const toggleSource = useCallback((id: string) => {
    setExtraIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      try {
        localStorage.setItem(CALENDAR_SOURCES_KEY, JSON.stringify(next));
      } catch {
        /* private mode — the choice just doesn't persist */
      }
      return next;
    });
  }, []);

  // Without extra sources the plain (unchanged) endpoint is used, so the Dagstart,
  // the slot finder and the task chips keep the exact response they expect.
  const defaultId = sources.find((s) => s.isDefault)?.id;
  const multiIds = activeExtraIds.length > 0 && defaultId ? [defaultId, ...activeExtraIds] : [];

  // Normalised to one shape so the component never branches: the plain endpoint
  // (no sources) is wrapped, the multi-source endpoint already answers this way.
  const eventsQuery = useQuery<CalendarViewResult>({
    queryKey: multiIds.length > 0 ? calendarKeys.viewFrom(from, to, multiIds) : calendarKeys.view(from, to),
    queryFn: async () =>
      multiIds.length > 0
        ? listCalendarFrom(from, to, multiIds)
        : { events: await listCalendar(from, to), failed: [] },
    enabled: ready,
    placeholderData: keepPreviousData,
    // The module is poll-based (docs/calendar.md): an invite or reschedule made
    // elsewhere surfaces within ~a minute without a manual reload.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const events = eventsQuery.data?.events ?? [];
  const failedSources = eventsQuery.data?.failed ?? [];

  // WLX tasks with a follow-up date in view — rendered as chips, never written
  // to Outlook. Only when the caller actually has the tasks module.
  const tasksQuery = useQuery({
    queryKey: ["calendar", "taskChips", from, to],
    queryFn: () => listTasksDueBetween(from, to),
    enabled: ready && canTasks,
    placeholderData: keepPreviousData,
  });
  const taskChips: CalendarTaskChip[] = (tasksQuery.data?.data ?? [])
    .filter((tk) => tk.dueDate)
    .map((tk) => ({
      id: tk.id,
      number: tk.number,
      title: tk.title,
      dueDate: tk.dueDate!,
      priority: tk.priority,
      isPlanned: Boolean(tk.calendarEventId),
    }));

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      navigate({ search: (prev) => applyParamUpdates(prev, updates), replace: true });
    },
    [navigate],
  );

  const onChanged = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["calendar", "view"] });
  }, [queryClient]);

  const onDisconnect = useCallback(() => {
    void disconnectCalendarAccount().then(() => {
      void queryClient.invalidateQueries({ queryKey: calendarKeys.account });
    });
  }, [queryClient]);

  return (
    <div className="wl-page" data-screen-label="Back-office Calendar">
      <CalendarClient
        status={statusQuery.data}
        events={ready ? events : []}
        tasks={taskChips}
        sources={sources}
        activeSourceIds={activeExtraIds}
        onToggleSource={toggleSource}
        failedSources={failedSources}
        search={search}
        updateParams={updateParams}
        isFetching={eventsQuery.isFetching}
        isLoading={ready && eventsQuery.isLoading}
        onChanged={onChanged}
        onDisconnect={onDisconnect}
      />
    </div>
  );
}
